import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Books, WarningCircle, FilePdf, FileImage, FileText, ArrowSquareOut } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/dms`;

const icon = (mime, size = 22) => {
  if (mime === 'application/pdf') return <FilePdf size={size} weight="duotone" className="text-rose-500 shrink-0" />;
  if ((mime || '').startsWith('image/')) return <FileImage size={size} weight="duotone" className="text-sky-500 shrink-0" />;
  return <FileText size={size} weight="duotone" className="text-zinc-400 shrink-0" />;
};

const fmtSize = (n) => {
  if (!n && n !== 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

// 公開バインダーページ — /b/{token}。ログイン不要・読み取り専用。
export default function SharedBundleView() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    axios.get(`${API}/shared-bundle/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setErr('このリンクは無効です'));   // 旧バインダー共有は廃止
  }, [token]);

  return (
    <div className="min-h-screen bg-zinc-100 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="rounded-lg bg-white shadow-sm border border-zinc-200 p-6" data-testid="shared-bundle-page">
          {err ? (
            <div className="py-12 text-center text-zinc-500" data-testid="shared-bundle-error">
              <WarningCircle size={36} className="mx-auto mb-2 text-rose-400" />
              <p className="text-sm">{err}</p>
            </div>
          ) : !data ? (
            <p className="py-12 text-center text-sm text-zinc-400">読み込み中…</p>
          ) : (
            <>
              <div className="flex items-center gap-2.5 mb-1">
                <Books size={26} weight="duotone" className="text-amber-600" />
                <h1 className="text-lg font-bold text-zinc-800" data-testid="shared-bundle-name">{data.name}</h1>
              </div>
              <p className="text-[11px] text-zinc-400 mb-5">
                共有バインダー（読み取り専用） · {data.documents.length}件
                {data.expires_at && ` · 有効期限 ${String(data.expires_at).slice(0, 10)}`}
              </p>
              {data.documents.length === 0 ? (
                <p className="py-8 text-center text-sm text-zinc-400">表示できる書類がありません</p>
              ) : (
                <div className="space-y-1.5">
                  {data.documents.map((d, i) => (
                    <a key={d.id} href={`${API}/shared-bundle/${token}/file/${d.id}`}
                      target="_blank" rel="noreferrer"
                      className="flex items-center gap-2.5 rounded-xl border border-zinc-200 px-3 py-2.5 hover:border-amber-400 hover:bg-amber-50/40 transition-colors group"
                      data-testid={`shared-doc-${d.id}`}>
                      <span className="text-[10px] font-black text-amber-600 tabular-nums w-5 text-center shrink-0">{i + 1}</span>
                      {icon(d.mime_type)}
                      <span className="min-w-0 flex-1">
                        <span className="block text-[13px] font-medium text-zinc-800 truncate group-hover:text-amber-700">{d.title}</span>
                        <span className="block text-[10px] text-zinc-400 font-mono">{d.document_number} · {d.doc_type_label} · {fmtSize(d.file_size)}</span>
                      </span>
                      <ArrowSquareOut size={15} className="text-zinc-300 group-hover:text-amber-600 shrink-0" />
                    </a>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-zinc-300 mt-6 text-center">Sensbey Document Desk</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
